//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import useIncrementalState from './useIncrementalState';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import SkillSelectors from 'common/redux/selectors/skill';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { useSelector } from 'react-redux';
import HeaderCheckboxWithDropdown from './HeaderCheckboxWithDropdown';

type PropTypes = {
    getExistingEntity: () => Promise<void>,
    EntityModel: Immutable.Map<*, *>,
    getNewEntity: ({ allExistingRecords: Array<Object> }) => Promise<void>
};

export const INCREMENTAL_PLAN_INDEX_MAP = Object.freeze({
    EXISTING_ENTITIES: 0,
    NEW_ENTITIES: 1
});

const useIncrementalEntityState = (props: PropTypes) => {
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);

    const teams = useSelector(state => teamsSelector(state, props));
    const skills = useSelector(state =>
        SkillSelectors.getEntities(state, props)
    );
    const isGlobalPersonnel = useSelector(state =>
        isGlobalPersonnelSelector(state, props)
    );

    const {
        onTableStateChange: onExistingTableStateChange,
        data: existingData,
        isLoading: existingLoading,
        totalCount: existingEntitiesCount,
        allRecords: allExistingRecords,
        toggleSelectAll: toggleSelectAllExistingData,
        toggleSelectPage: toggleSelectExistingPageData,
        toggleRowSelection: toggleExistingDataRowSelection
    } = useIncrementalState({
        fetchRecords: props.getExistingEntity,
        EntitiesModel: props.EntityModel
    });

    const _getNewEntities = () => props.getNewEntity({ allExistingRecords });

    const {
        onTableStateChange: onNewTableStateChange,
        data: newData,
        isLoading: newLoading,
        totalCount: newEntitiesCount,
        toggleRowSelection: toggleNewDataRowSelection,
        toggleSelectAll: toggleSelectAllNewData,
        toggleSelectPage: toggleSelectNewPageData,
        allRecords: allNewRecords
    } = useIncrementalState({
        fetchRecords: _getNewEntities,
        EntitiesModel: props.EntityModel
    });

    const allNewRecordsLength = allNewRecords?.length;
    const existingRecordsLength = allExistingRecords?.length;

    React.useEffect(() => {
        if (
            !existingLoading &&
            !newLoading &&
            existingRecordsLength &&
            allNewRecordsLength === undefined
        ) {
            onNewTableStateChange({});
        }
    }, [
        existingLoading,
        newLoading,
        onNewTableStateChange,
        allNewRecordsLength,
        existingRecordsLength
    ]);

    let conditionalData = {
        selectedRowIds: existingData.selectedRowIds,
        onTableStateChange: onExistingTableStateChange,
        data: existingData,
        isLoading: existingLoading,
        toggleRowSelection: React.useCallback(
            (id, selected) => {
                toggleExistingDataRowSelection(id, !selected);
            },
            [toggleExistingDataRowSelection]
        ),
        toggleSelectAll: React.useCallback(
            checked => {
                toggleSelectAllExistingData(!checked);
            },
            [toggleSelectAllExistingData]
        ),
        toggleSelectPage: React.useCallback(
            checked => {
                toggleSelectExistingPageData(!checked);
            },
            [toggleSelectExistingPageData]
        ),
        rowSelectedType: (() => {
            if (existingData.selectedRowIds.length === 0) {
                return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.ALL;
            }
            if (
                existingData.selectedRowIds.length &&
                existingData.selectedRowIds.length !== allExistingRecords.length
            ) {
                return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.SOME;
            }
            return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.NONE;
        })()
    };

    const getExistingSelectedRows = React.useCallback(() => {
        return allExistingRecords
            .map(({ id }) => id)
            .filter(id => !existingData.selectedRowIds.find(_id => _id === id));
    }, [allExistingRecords, existingData.selectedRowIds]);

    const getNewSelectedRows = React.useCallback(() => {
        return newData.selectedRowIds;
    }, [newData.selectedRowIds]);

    if (activeTabIndex === INCREMENTAL_PLAN_INDEX_MAP.NEW_ENTITIES) {
        conditionalData = {
            selectedRowIds: newData.selectedRowIds,
            onTableStateChange: onNewTableStateChange,
            data: newData,
            isLoading: newLoading,
            toggleRowSelection: toggleNewDataRowSelection,
            toggleSelectAll: toggleSelectAllNewData,
            toggleSelectPage: toggleSelectNewPageData,
            rowSelectedType: (() => {
                if (newData.selectedRowIds.length === allNewRecords?.length) {
                    return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.ALL;
                } else if (newData.selectedRowIds.length) {
                    return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.SOME;
                } else {
                    return HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE.NONE;
                }
            })()
        };
    }

    return {
        existingData,
        activeTabIndex,
        setActiveTabIndex,
        newLoading,
        existingLoading,
        teams,
        skills,
        isGlobalPersonnel,
        newEntitiesCount,
        existingEntitiesCount,
        selectedRowCount: newData.selectedRowIds.length,
        removedRowsCount: existingData.selectedRowIds.length,
        getExistingSelectedRows,
        getNewSelectedRows,
        allEntitiesCount:
            newData.selectedRowIds.length +
            ((existingEntitiesCount || 0) - existingData.selectedRowIds.length),
        ...conditionalData
    };
};

export default useIncrementalEntityState;
